import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth.jsx';
import { OrderNotificationsProvider } from './hooks/useOrderNotifications.jsx';
import OrderNotificationToast from './components/OrderNotificationToast.jsx';
import ScrollToHashOrTop from './components/ScrollToHashOrTop.jsx';

// Keep these in the main bundle — they're the highest-traffic public pages
// (LandingPage = first impression for new visitors, PublicMenuPage = where
// AdSense runs and where the crawler must see content without round-trips).
import LandingPage from './pages/LandingPage.jsx';
import PublicMenuPage from './pages/PublicMenuPage.jsx';

// Everything else is lazy-loaded into its own chunk on first visit.
// Auth pages are tiny so they share one chunk via webpackChunkName hints.
const HowToPage              = lazy(() => import('./pages/HowToPage.jsx'));
const PrivacyPolicyPage      = lazy(() => import('./pages/PrivacyPolicyPage.jsx'));
const TermsPage              = lazy(() => import('./pages/TermsPage.jsx'));
const RefundPolicyPage       = lazy(() => import('./pages/RefundPolicyPage.jsx'));
const SubscriptionPolicyPage = lazy(() => import('./pages/SubscriptionPolicyPage.jsx'));

const LoginPage              = lazy(() => import('./pages/LoginPage.jsx'));
const RegisterPage           = lazy(() => import('./pages/RegisterPage.jsx'));
const ForgotPasswordPage     = lazy(() => import('./pages/ForgotPasswordPage.jsx'));
const ResetPasswordPage      = lazy(() => import('./pages/ResetPasswordPage.jsx'));
const VerifyEmailPage        = lazy(() => import('./pages/VerifyEmailPage.jsx'));

const DashboardPage          = lazy(() => import('./pages/DashboardPage.jsx'));
const MenuEditorPage         = lazy(() => import('./pages/MenuEditorPage.jsx'));
const OrdersPage             = lazy(() => import('./pages/OrdersPage.jsx'));
const SettingsPage           = lazy(() => import('./pages/SettingsPage.jsx'));
const AdminPage              = lazy(() => import('./pages/AdminPage.jsx'));

// Tiny placeholder while a lazy chunk is fetched — same look as the auth
// guard's "Loading…" so route transitions feel consistent.
function PageFallback() {
  return <div className="loading" style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Loading…</div>;
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicOnlyRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading">Loading...</div>;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

export default function App() {
  return (
    <OrderNotificationsProvider>
      <ScrollToHashOrTop />
      <Suspense fallback={<PageFallback />}>
        <Routes>
          {/* Public — in main bundle */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/m/:slug" element={<PublicMenuPage />} />

          {/* Public — lazy */}
          <Route path="/how-to" element={<HowToPage />} />
          <Route path="/how-it-works" element={<Navigate to="/how-to" replace />} />

          {/* Legal */}
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/privacy-policy" element={<Navigate to="/privacy" replace />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/terms-and-conditions" element={<Navigate to="/terms" replace />} />
          <Route path="/refund" element={<RefundPolicyPage />} />
          <Route path="/refund-policy" element={<Navigate to="/refund" replace />} />
          <Route path="/subscription" element={<SubscriptionPolicyPage />} />
          <Route path="/subscription-policy" element={<Navigate to="/subscription" replace />} />

          {/* Auth (only for non-logged-in) */}
          <Route path="/login" element={<PublicOnlyRoute><LoginPage /></PublicOnlyRoute>} />
          <Route path="/register" element={<PublicOnlyRoute><RegisterPage /></PublicOnlyRoute>} />
          <Route path="/forgot-password" element={<PublicOnlyRoute><ForgotPasswordPage /></PublicOnlyRoute>} />
          <Route path="/reset-password" element={<PublicOnlyRoute><ResetPasswordPage /></PublicOnlyRoute>} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />

          {/* Protected */}
          <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
          <Route path="/orders" element={<ProtectedRoute><OrdersPage /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
          <Route path="/menus/:id" element={<ProtectedRoute><MenuEditorPage /></ProtectedRoute>} />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>

      {/* Floating order toast — auto-hidden when not on a protected page */}
      <OrderNotificationToast />
    </OrderNotificationsProvider>
  );
}
